import { useState } from "react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useLang } from "@/i18n";

const stepIcons = [
  "mdi:lightbulb-outline",
  "mdi:database-search",
  "mdi:handshake-outline",
  "mdi:pencil-ruler",
  "mdi:rocket-launch-outline",
  "mdi:chart-line",
  "mdi:archive-check-outline",
];

const ease = [0.4, 0, 0.2, 1];

export default function HowWeWork() {
  const { t } = useLang();
  const steps = t.howWeWork.steps.map((s, i: number) => ({ ...s, icon: stepIcons[i] }));
  const [active, setActive] = useState<number | null>(null);

  return (
    <section id="how-we-work" style={{ background: "#090909", overflow: "hidden" }}>
      {/* Top accent bar */}
      <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(244,68,12,0.4), transparent)" }} />
      
      <div className="max-w-7xl mx-auto section-pad">

        {/* Header */}
        <div className="how-grid" style={{ marginBottom: "4rem" }}>
          <div>
            <motion.div
              initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.8, ease }}
              style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}
            >
              <div style={{ width: 32, height: 1.5, background: "#f4440c" }} />
              <span style={{ fontSize: "0.82rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f4440c", fontWeight: 400 }}>
              {t.howWeWork.eyebrow}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1, ease }}
              style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "clamp(2.4rem, 4.5vw, 3.8rem)", fontWeight: 700, color: "#fff", lineHeight: 1.0, letterSpacing: "-0.04em", margin: 0 }}
            >
              {t.howWeWork.headline1}<br />
              <span style={{ color: "transparent", WebkitTextStroke: "1.5px #f4440c" }}>{t.howWeWork.headline2}</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.25, ease }}
            style={{ fontSize: "0.97rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.8, alignSelf: "end" }}
          >
            {t.howWeWork.body}
            <br /><br />
            <em style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "1.1rem", color: "rgba(255,255,255,0.25)", fontStyle: "italic" }}>
              {t.howWeWork.tagline}
            </em>
          </motion.p>
        </div>

        {/* Steps */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: Math.min(i * 0.06, 0.3), ease }}
            >
              <button
                onClick={() => setActive(active === i ? null : i)}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  cursor: "pointer",
                  padding: "0",
                  textAlign: "left",
                }}
              >
                <div
                  className="how-step-row"
                  style={{ gap: "2rem", transition: "background 0.3s" }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.paddingLeft = "0.5rem"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.paddingLeft = "0"; }}
                >
                  {/* Number */}
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "1rem", color: active === i ? "#f4440c" : "rgba(255,255,255,0.2)", letterSpacing: "0.08em", fontWeight: 400, transition: "color 0.3s" }}>
                    {step.num}
                  </span>

                  {/* Title */}
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <Icon icon={step.icon} style={{ fontSize: 20, color: active === i ? "#f4440c" : "rgba(255,255,255,0.3)", transition: "color 0.3s", flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: "1rem", fontWeight: 600, color: active === i ? "#fff" : "rgba(255,255,255,0.8)", transition: "color 0.3s", letterSpacing: "0.01em" }}>
                        {step.title}
                      </div>
                      <div style={{ fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", marginTop: "0.2rem" }}>
                      {step.enLabel}
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.35)", lineHeight: 1.6, margin: 0 }}>
                    {step.summary}
                  </p>

                  {/* Toggle icon */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Icon
                      icon={active === i ? "mdi:minus" : "mdi:plus"}
                      style={{ fontSize: 18, color: active === i ? "#f4440c" : "rgba(255,255,255,0.25)", transition: "color 0.3s" }}
                    />
                  </div>
                </div>
              </button>

              {/* Expanded detail */}
              {active === i && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease }}
                  style={{
                    padding: "0 0 2rem 1rem",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    marginTop: "-1px",
                  }}
                >
                  <div style={{
                    background: "rgba(244,68,12,0.04)",
                    border: "1px solid rgba(244,68,12,0.12)",
                    padding: "1.5rem 2rem",
                    maxWidth: 680,
                  }}>
                    <p style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.62)", lineHeight: 1.85, margin: 0, fontWeight: 400 }}>
                      {step.detail}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2, ease }}
          className="how-cta-block" style={{ marginTop: "4rem", border: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "1.8rem", fontWeight: 400, color: "#fff", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
              {t.howWeWork.ctaTitle}
            </div>
            <div style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.38)", marginTop: "0.4rem" }}>
              {t.howWeWork.ctaSub}
            </div>
          </div>
          <button
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            style={{
              background: "#f4440c",
              color: "#fff",
              border: "none",
              padding: "1rem 2.5rem",
              fontSize: "0.82rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "background 0.3s",
              flexShrink: 0,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#d93a0a"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "#f4440c"; }}
          >
            {t.howWeWork.ctaBtn}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
